import React, {useEffect, useRef} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import socket, {emitNow, OFFLINE_TOAST} from "../utils/clientSocket";
import {gameState} from "./Game";
import {useToasts} from "./ui/Toasts";
import Avatar from "./ui/Avatar";
import Icon from "./ui/Icon";
import {setOwnOffer} from "../utils/offerStore";
import {setActiveGame} from "../utils/activeGameStore";
import {formatTimeControl} from "../utils/chessFunctions";
import {playSound, preloadSounds} from "../utils/sounds";
import {addUnread, setFriendRequestCount} from "../utils/notificationStore";
import {fetchFriendRequests} from "../clients/FriendClient";
import {useUser} from "../UserContext";

const describeOffer = (offer) => {
    const variant = offer.variant === "jumpstart" ? "Jumpstart" : "Standard";
    return `${formatTimeControl(offer.baseTime, offer.increment)} · ${variant}`;
};

/**
 * Listens for site wide events (games starting, friend challenges, friend messages) and shows them as toasts.
 * It renders nothing itself.
 */
const FriendChallenges = () => {
    const navigate = useNavigate()
    const location = useLocation();
    const user = useUser();
    const {push, dismiss} = useToasts();
    const acceptedOfferIds = useRef([])
    const ownChallengeId = useRef(null);
    const locationRef = useRef(location);
    locationRef.current = location;

    // The login ran out while the site was open (see UserContext): say so once, with a way to log in again
    useEffect(() => {
        if (!user?.loginExpired) return;
        push({
            id: "login-expired",
            tone: "sun",
            title: "Your login has expired",
            text: "You are playing as a guest now. Log in again to save your games and chat with friends.",
            icon: <Icon name="info" size={22}/>,
            duration: null,
            actions: [{label: "Log in", variant: "primary", onClick: () => navigate("/my-profile")}]
        });
    }, [user?.loginExpired, push, navigate]);

    // Tell the player when the connection to the server is lost (after a short grace period, so a quick blip or the
    // first connect on page load stays silent), and when it is back
    useEffect(() => {
        let timer = null;
        let shown = false;
        const onDisconnect = () => {
            clearTimeout(timer);
            timer = setTimeout(() => {
                shown = true;
                push({
                    id: "connection",
                    tone: "sun",
                    title: "Reconnecting…",
                    text: "Lost the connection to the server. New games can start once it is back.",
                    icon: <span className="spinner spinner--sm"/>,
                    duration: null
                });
            }, 2000);
        };
        const onConnect = () => {
            clearTimeout(timer);
            if (shown) {
                shown = false;
                dismiss("connection");
                push({id: "connection-back", tone: "blossom", title: "Connected again", duration: 2500});
            }
        };
        socket.on("disconnect", onDisconnect);
        socket.on("connect", onConnect);
        if (!socket.connected) onDisconnect();
        return () => {
            clearTimeout(timer);
            socket.off("disconnect", onDisconnect);
            socket.off("connect", onConnect);
        };
    }, [push, dismiss]);

    useEffect(() => {
        preloadSounds();
    }, []);

    // Pending friend requests shown as a badge in the header
    useEffect(() => {
        if (!user?.isLoggedIn) {
            setFriendRequestCount(0);
            return;
        }
        fetchFriendRequests()
            .then((requests) => setFriendRequestCount(requests.length))
            .catch(() => setFriendRequestCount(0));
    }, [user?.isLoggedIn]);

    useEffect(() => {
        const handleGameStarted = ({gameId, gameLength, isWhite, fen, initialFen, currentTurn, eloDeltas, moves, opponent}) => {
            setOwnOffer(null);
            if (ownChallengeId.current) {
                dismiss("own-challenge");
                ownChallengeId.current = null;
            }
            const state = {
                gameId,
                fen,
                initialFen,
                gameLength,
                gameState: gameState.IN_PROGRESS,
                currentTurn,
                eloDeltas,
                moves,
                opponent,
                orientation: isWhite ? "white" : "black",
                color: isWhite ? "w" : "b",
                startedAt: Date.now()
            };
            setActiveGame(state);
            navigate("/play", {state})
        };

        const handleChallengeCreated = ({offer, isSuccessful}) => {
            if (!isSuccessful) {
                push({
                    tone: "danger",
                    title: "Challenge not sent",
                    text: "Your friend seems to be offline right now.",
                    icon: <Icon name="info" size={22} style={{color: "var(--danger)"}}/>
                });
                return;
            }
            ownChallengeId.current = offer.offerId;
            push({
                id: "own-challenge",
                duration: null,
                tone: "sun",
                title: `Challenge sent${offer.friendName ? ` to ${offer.friendName}` : ""}`,
                text: `${describeOffer(offer)}. Waiting for an answer…`,
                icon: <span className="spinner" style={{marginTop: 2}}/>,
                dismissible: false,
                actions: [{
                    label: "Cancel challenge",
                    onClick: () => {
                        socket.emit("cancelOffer");
                        ownChallengeId.current = null;
                    }
                }]
            });
        };

        const handleChallengeReceived = (offer) => {
            playSound("notify");
            push({
                id: `challenge-${offer.offerId}`,
                duration: null,
                tone: "blossom",
                title: `${offer.username || "A friend"} challenges you!`,
                text: describeOffer(offer),
                icon: <Avatar name={offer.username} size={36}/>,
                onDismiss: () => socket.emit("denyFriendChallenge", {offerId: offer.offerId}),
                actions: [
                    {
                        label: "Accept",
                        variant: "primary",
                        onClick: () => {
                            if (acceptedOfferIds.current.includes(offer.offerId)) return;
                            if (!emitNow("acceptGameOffer", {offerId: offer.offerId})) {
                                push(OFFLINE_TOAST);
                                return;
                            }
                            acceptedOfferIds.current.push(offer.offerId);
                        }
                    },
                    {
                        label: "Decline",
                        onClick: () => socket.emit("denyFriendChallenge", {offerId: offer.offerId})
                    }
                ]
            });
        };

        const handleChallengeRemoved = (offerId) => {
            if (ownChallengeId.current === offerId) {
                ownChallengeId.current = null;
                dismiss("own-challenge");
                push({title: "Challenge closed", text: "Your friend declined or the challenge was cancelled.", tone: "sun"});
            } else {
                dismiss(`challenge-${offerId}`);
            }
        };

        const handleFriendMessage = (message) => {
            const sender = message.senderName;
            const path = locationRef.current.pathname;
            if (path === `/messages/${encodeURIComponent(sender)}` || path === `/messages/${sender}`) return;
            addUnread(sender);
            playSound("notify");
            push({
                id: `message-${sender}`,
                tone: "blossom",
                title: `${sender}`,
                text: message.message,
                icon: <Avatar name={sender} size={36}/>,
                duration: 7000,
                actions: [{label: "Reply", variant: "primary", onClick: () => navigate(`/messages/${encodeURIComponent(sender)}`)}]
            });
        };

        const handleActionRejected = ({message}) => {
            push({
                tone: "danger",
                title: "That did not work",
                text: message,
                icon: <Icon name="info" size={22} style={{color: "var(--danger)"}}/>
            });
        };

        socket.on("actionRejected", handleActionRejected);
        socket.on("gameStarted", handleGameStarted);
        socket.on("friendChallengeCreated", handleChallengeCreated);
        socket.on("friendChallengeReceived", handleChallengeReceived);
        socket.on("friendChallengeRemoved", handleChallengeRemoved);
        socket.on("friendMessage", handleFriendMessage);

        return () => {
            socket.off("actionRejected", handleActionRejected);
            socket.off("gameStarted", handleGameStarted);
            socket.off("friendChallengeCreated", handleChallengeCreated);
            socket.off("friendChallengeReceived", handleChallengeReceived);
            socket.off("friendChallengeRemoved", handleChallengeRemoved);
            socket.off("friendMessage", handleFriendMessage);
        }
    }, [navigate, push, dismiss]);

    return null;
}

export default FriendChallenges;
